"use client";

import { useState } from "react";
import {
  Checkbox,
  type Option,
  Panel,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  ExampleButton,
  NoticeList,
  SmallButton,
  StatementCard,
} from "@/components/tools/publishing-statements/kit";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type NameStyle,
  sharedInitials,
} from "@/lib/tool-kit/publishing/authors";
import {
  type Declaration,
  INTEREST_TYPES,
  type InterestStyle,
  type InterestType,
  interestCounts,
  interestStatement,
} from "@/lib/tool-kit/publishing/competing-interests";
import { plainParagraph } from "@/lib/tool-kit/publishing/rich-text";

/** English strings. A translated content file overrides these through `ui`. */
export const CONFLICT_OF_INTEREST_STATEMENT_GENERATOR_STRINGS = {
  authorPanel: "Author {number}",
  nameLabel: "Name",
  namePlaceholder: "e.g. Maya Okafor",
  noDeclarations:
    "Nothing to declare for this author. Add a declaration if there is one.",
  addDeclaration: "Add a declaration",
  removeDeclaration: "Remove declaration",
  removeAuthor: "Remove author",
  removeAuthorAria: "Remove author {number}",
  addAuthor: "Add author",
  typeLabel: "Type of interest",
  entityLabel: "Organization or description",
  entityPlaceholder: "e.g. Acme Biologics",
  detailsLabel: "Details (optional)",
  detailsPlaceholder: "e.g. paid to the author's institution",
  outsideLabel: "Outside the submitted work",
  styleLabel: "Wording",
  styleNature: "Standard sentences (Nature Portfolio)",
  styleElsevier: "Elsevier declaration",
  namesLabel: "Names",
  namesFull: "Full names",
  namesInitials: "Initials",
  tryExample: "Try an example",
  exampleNames: ["Maya Okafor", "Daniel Ruiz", "Priya Raman"],
  exampleCompany: "Acme Biologics",
  exampleDetails: "paid to her institution",
  results: "Your statement",
  headingNature: "Competing interests",
  headingElsevier: "Declaration of competing interest",
  copy: "Copy statement",
  copied: "Copied!",
  noticeIcmje:
    "Medical journals that follow the ICMJE also ask every author to complete the ICMJE Disclosure Form. Its first item covers all support for this manuscript with no time limit; the other twelve cover the past 36 months.",
  noticeDoubt:
    "When you are unsure whether a relationship counts, declare it — the ICMJE form itself says it is preferable to list it.",
  noticePlaceholder:
    "Replace each [details] placeholder with the organization or interest before you submit.",
  sharedInitialsWarning:
    "More than one author has the initials {initials}. Use full names, or tell them apart by hand.",
  listSeparator: ", ",
  typeLabels: {
    funding: "Research funding or grants",
    employment: "Employment (current)",
    pastEmployment: "Employment (past)",
    consulting: "Consulting fees",
    honoraria: "Honoraria or speaker fees",
    stock: "Stock or stock options",
    patent: "Patent (planned, pending or issued)",
    royalties: "Royalties or licenses",
    advisory: "Advisory or monitoring board",
    testimony: "Expert testimony",
    travel: "Travel or meeting support",
    leadership: "Leadership or fiduciary role",
    personal: "Personal relationship",
    other: "Other interest",
  },
  sentences: {
    funding: "{name} has received research funding from {entity}",
    employment: "{name} is an employee of {entity}",
    pastEmployment: "{name} was previously employed by {entity}",
    consulting: "{name} has received consulting fees from {entity}",
    honoraria: "{name} has received honoraria or speaker fees from {entity}",
    stock: "{name} holds stock or stock options in {entity}",
    patent: "{name} is a named inventor on {entity}",
    royalties: "{name} receives royalties or license fees from {entity}",
    advisory:
      "{name} has served on an advisory board or data safety monitoring board for {entity}",
    testimony: "{name} has received payment for expert testimony from {entity}",
    travel:
      "{name} has received support for attending meetings or travel from {entity}",
    leadership: "{name} holds a leadership or fiduciary role in {entity}",
    personal: "{name} declares a personal relationship: {entity}",
    other: "{name} declares {entity}",
  },
  details: " ({details})",
  outside: ", outside the submitted work",
  placeholderEntity: "[details]",
  noneNature:
    "one: The author declares no competing interests.|other: The authors declare no competing interests.",
  introNature:
    "one: The author declares the following competing interests:|other: The authors declare the following competing interests:",
  noneElsevier:
    "one: The author declares that they have no known competing financial interests or personal relationships that could have appeared to influence the work reported in this article.|other: The authors declare that they have no known competing financial interests or personal relationships that could have appeared to influence the work reported in this article.",
  introElsevier:
    "one: The author declares the following financial or non-financial interests which may be considered as potential conflicts of interest:|other: The authors declare the following financial or non-financial interests which may be considered as potential conflicts of interest:",
  remaining:
    "one: The remaining author declares no competing interests.|other: The remaining authors declare no competing interests.",
} as const;

type Strings = typeof CONFLICT_OF_INTEREST_STATEMENT_GENERATOR_STRINGS;

interface DeclarationRow extends Declaration {
  id: number;
}

interface AuthorRow {
  id: number;
  name: string;
  declarations: DeclarationRow[];
}

const MAX_AUTHORS = 40;

function emptyDeclaration(id: number): DeclarationRow {
  return { id, type: "consulting", entity: "", details: "", outside: false };
}

function DeclarationFields({
  row,
  strings,
  onChange,
  onRemove,
}: {
  row: DeclarationRow;
  strings: Strings;
  onChange: (patch: Partial<DeclarationRow>) => void;
  onRemove: () => void;
}) {
  const typeOptions: Option<InterestType>[] = INTEREST_TYPES.map((type) => ({
    value: type,
    label: strings.typeLabels[type],
  }));
  return (
    <div className="space-y-3 rounded-lg border border-border border-dashed p-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <SelectField<InterestType>
          id={`coi-type-${row.id}`}
          label={strings.typeLabel}
          onChange={(type) => onChange({ type })}
          options={typeOptions}
          value={row.type}
        />
        <TextField
          id={`coi-entity-${row.id}`}
          label={strings.entityLabel}
          onChange={(entity) => onChange({ entity })}
          placeholder={strings.entityPlaceholder}
          value={row.entity}
        />
        <TextField
          id={`coi-details-${row.id}`}
          label={strings.detailsLabel}
          onChange={(details) => onChange({ details })}
          placeholder={strings.detailsPlaceholder}
          value={row.details}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Checkbox
          checked={row.outside}
          label={strings.outsideLabel}
          onChange={(outside) => onChange({ outside })}
        />
        <SmallButton label={strings.removeDeclaration} onClick={onRemove} />
      </div>
    </div>
  );
}

function noticesFor(
  rows: readonly AuthorRow[],
  nameStyle: NameStyle,
  statement: string,
  strings: Strings
): string[] {
  const notes: string[] = [];
  if (statement.includes(strings.placeholderEntity)) {
    notes.push(strings.noticePlaceholder);
  }
  const shared = sharedInitials(rows.map((row) => row.name));
  if (nameStyle === "initials" && shared.length > 0) {
    notes.push(
      t(strings.sharedInitialsWarning, {
        initials: shared.join(strings.listSeparator),
      })
    );
  }
  notes.push(strings.noticeIcmje, strings.noticeDoubt);
  return notes;
}

export default function ConflictOfInterestStatementGeneratorWidget() {
  const strings = useToolUi(CONFLICT_OF_INTEREST_STATEMENT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [rows, setRows] = useState<AuthorRow[]>([
    { id: 1, name: "", declarations: [] },
    { id: 2, name: "", declarations: [] },
  ]);
  const [nextId, setNextId] = useState(3);
  const [style, setStyle] = useState<InterestStyle>("nature");
  const [nameStyle, setNameStyle] = useState<NameStyle>("full");

  const counts = interestCounts(rows);
  const frame =
    style === "nature"
      ? {
          none: tPlural(strings.noneNature, locale, counts.authors).trim(),
          intro: tPlural(strings.introNature, locale, counts.authors).trim(),
          remaining: tPlural(
            strings.remaining,
            locale,
            counts.remaining
          ).trim(),
        }
      : {
          none: tPlural(strings.noneElsevier, locale, counts.authors).trim(),
          intro: tPlural(strings.introElsevier, locale, counts.authors).trim(),
          remaining: tPlural(
            strings.remaining,
            locale,
            counts.remaining
          ).trim(),
        };
  const statement = interestStatement(rows, nameStyle, strings, frame);
  const notes = noticesFor(rows, nameStyle, statement, strings);

  function takeId(): number {
    setNextId((id) => id + 1);
    return nextId;
  }

  function updateAuthor(id: number, change: (row: AuthorRow) => AuthorRow) {
    setRows((current) =>
      current.map((row) => (row.id === id ? change(row) : row))
    );
  }

  function updateDeclaration(
    authorId: number,
    declarationId: number,
    patch: Partial<DeclarationRow>
  ) {
    updateAuthor(authorId, (row) => ({
      ...row,
      declarations: row.declarations.map((item) =>
        item.id === declarationId ? { ...item, ...patch } : item
      ),
    }));
  }

  function loadExample() {
    const [first, second, third] = strings.exampleNames;
    const start = nextId;
    setRows([
      {
        id: start,
        name: first,
        declarations: [
          {
            id: start + 1,
            type: "consulting",
            entity: strings.exampleCompany,
            details: strings.exampleDetails,
            outside: true,
          },
        ],
      },
      {
        id: start + 2,
        name: second,
        declarations: [
          {
            id: start + 3,
            type: "stock",
            entity: strings.exampleCompany,
            details: "",
            outside: false,
          },
        ],
      },
      { id: start + 4, name: third, declarations: [] },
    ]);
    setNextId(start + 5);
  }

  const styleOptions: Option<InterestStyle>[] = [
    { value: "nature", label: strings.styleNature },
    { value: "elsevier", label: strings.styleElsevier },
  ];
  const nameOptions: Option<NameStyle>[] = [
    { value: "full", label: strings.namesFull },
    { value: "initials", label: strings.namesInitials },
  ];

  return (
    <div className="space-y-6">
      {rows.map((row, index) => (
        <Panel
          key={row.id}
          title={t(strings.authorPanel, { number: index + 1 })}
        >
          <TextField
            id={`coi-name-${row.id}`}
            label={strings.nameLabel}
            onChange={(name) => updateAuthor(row.id, (r) => ({ ...r, name }))}
            placeholder={strings.namePlaceholder}
            value={row.name}
          />
          {row.declarations.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {strings.noDeclarations}
            </p>
          ) : null}
          {row.declarations.map((declaration) => (
            <DeclarationFields
              key={declaration.id}
              onChange={(patch) =>
                updateDeclaration(row.id, declaration.id, patch)
              }
              onRemove={() =>
                updateAuthor(row.id, (r) => ({
                  ...r,
                  declarations: r.declarations.filter(
                    (item) => item.id !== declaration.id
                  ),
                }))
              }
              row={declaration}
              strings={strings}
            />
          ))}
          <div className="flex flex-wrap gap-3">
            <SmallButton
              label={strings.addDeclaration}
              onClick={() => {
                const id = takeId();
                updateAuthor(row.id, (r) => ({
                  ...r,
                  declarations: [...r.declarations, emptyDeclaration(id)],
                }));
              }}
            />
            <SmallButton
              ariaLabel={t(strings.removeAuthorAria, { number: index + 1 })}
              disabled={rows.length <= 1}
              label={strings.removeAuthor}
              onClick={() =>
                setRows((current) => current.filter((r) => r.id !== row.id))
              }
            />
          </div>
        </Panel>
      ))}
      <SmallButton
        disabled={rows.length >= MAX_AUTHORS}
        label={strings.addAuthor}
        onClick={() => {
          const id = takeId();
          setRows((current) => [
            ...current,
            { id, name: "", declarations: [] },
          ]);
        }}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.styleLabel}
          name="coi-style"
          onChange={setStyle}
          options={styleOptions}
          value={style}
        />
        <PillGroup
          label={strings.namesLabel}
          name="coi-names"
          onChange={setNameStyle}
          options={nameOptions}
          value={nameStyle}
        />
      </div>
      <ExampleButton label={strings.tryExample} onClick={loadExample} />

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <StatementCard
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          heading={
            style === "nature" ? strings.headingNature : strings.headingElsevier
          }
          paragraphs={[plainParagraph(statement)]}
        />
        <NoticeList items={notes} />
      </section>
    </div>
  );
}
